import { clsx, type ClassValue } from 'clsx'
import { twMerge } from 'tailwind-merge'

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

function utf8ToBase64(str: string) {
  return btoa(encodeURIComponent(str))
}

export const hashObject = (obj: Record<string, unknown>) => {
  try {
    const stringified = JSON.stringify(obj)
    const hashed = utf8ToBase64(stringified)
    return hashed
  } catch (error) {
    console.error((error as Error).message)
  }
}

function base64ToUtf8(base64String: string) {
  const binaryString = atob(base64String)
  const decodedString = decodeURIComponent(binaryString)
  return decodedString
}

export const decodeHashedObject = <T = Record<string, unknown>>(
  hash: string
) => {
  try {
    const decoded = base64ToUtf8(hash)
    const obj = JSON.parse(decoded)
    return obj as T
  } catch (error) {
    console.error((error as Error).message)
  }
}

export const translateNumberToArabic = (num: string) => {
  const arabicNumbers = ['٠', '١', '٢', '٣', '٤', '٥', '٦', '٧', '٨', '٩']
  return num
    .split('')
    .map((digit) => (/\d/.test(digit) ? arabicNumbers[Number(digit)] : digit))
    .join('')
}

export const convertToMilliseconds = (
  value: number,
  unit: 'minutes' | 'hours' | 'days'
) => {
  const multipliers = {
    minutes: 60 * 1000,
    hours: 60 * 60 * 1000,
    days: 24 * 60 * 60 * 1000,
  }
  return value * multipliers[unit]
}

export const formatDate = (dateString: string | Date): string => {
  try {
    const date = new Date(dateString)

    // Check if date is valid
    if (isNaN(date.getTime())) {
      return String(dateString) // Convert to string if invalid date
    }

    const day = date.getDate().toString().padStart(2, '0')
    const month = (date.getMonth() + 1).toString().padStart(2, '0')
    const year = date.getFullYear()

    return `${day}/${month}/${year}`
  } catch (error) {
    console.error('Error formatting date:', error)
    return String(dateString) // Convert to string if error occurs
  }
}

export const formatDateWithTime = (dateString: string | Date): string => {
  try {
    const date = new Date(dateString)

    // Check if date is valid
    if (isNaN(date.getTime())) {
      return String(dateString) // Convert to string if invalid date
    }

    const day = date.getDate().toString().padStart(2, '0')
    const month = (date.getMonth() + 1).toString().padStart(2, '0')
    const year = date.getFullYear()

    const hours = date.getHours().toString().padStart(2, '0')
    const minutes = date.getMinutes().toString().padStart(2, '0')

    return `${day}/${month}/${year} ${hours}:${minutes}`
  } catch (error) {
    console.error('Error formatting date with time:', error)
    return String(dateString) // Convert to string if error occurs
  }
}

export function areGuidsEqual(guid1: any, guid2: any) {
  if (!guid1 || !guid2) return false
  return guid1.trim().toLowerCase() === guid2.trim().toLowerCase()
}
